import React, {createRef, useState, useContext} from 'react';
import {useAuth0} from "@auth0/auth0-react";
import {useQueryClient} from "react-query";
import axios from "axios";
import moment from "moment";
import _ from "underscore";

import MaterialTable from "@material-table/core";
import {Button, Icon, IconButton, TablePagination, Tooltip, useTheme} from "@material-ui/core";
import SystemContext from "../../../SystemContext";
import { getApiSitePrefix } from "../../../functions/SiteDefaults";
import { defaultComponents } from '../../../controls/Material-Table-Components';
import { useCompanyUsers, useDeleteCompanyUser} from "../../../services/auth";
import { ValidatorColumn, ColumnWidth } from "../../../blocks/ColumnHelper";
import {
  ComboSelect,
} from "../../../controls";
import UserDetails from "./UserDetailsForm";


const apiSitePrefix = getApiSitePrefix();

const getUserClass = (company) => {
  if (company.isGsa)
    return "GSA";
  if (company.isClient || company.isShipper || company.isConsignee)
    return "CUSTOMER";
  if (company.isSupplier || company.isFuelSupplier || company.isPermitSupplier || company.isAirline)
    return "SUPPLIER";
  return "OPERATOR"
}

const CompanyUserList = (_props) => {
  const {isAuthenticated} = useAuth0();
  const SystemData = useContext(SystemContext);
  const theme = useTheme();
  //const validatorColumn = ValidatorColumn(CompanyContactsValidator, true);

  const props = {
    ..._props
  }
  const classes = props.classes;

  const [state, setState] = useState({
    canEdit: props.canEdit,
    activeRow: null
  });
  const [dialogStates, setDialogStates] = useState({
    showDialog: false
  });

  const [companyRecord, setCompanyRecord] = useState(props.record)

  const eventBus = SystemData.eventBus;
  if (eventBus) {
    eventBus.bind('CompanyManagementList.rowSelected', (record)=> {
      setCompanyRecord(record);
    });
  }

  const queryClient = useQueryClient();
  const tableRef = createRef({state: {lastEditingRow: false}});

  const handleDeleteCompanyUser = useDeleteCompanyUser();

  const extraKeys = [companyRecord.companyUuid];
  const companyUsers = useCompanyUsers({queryClient, extraKeys, useCache: false});

  const getUserRoles = async (userSub) => {
    try {
      const {data} = await axios.get(`${apiSitePrefix}/getuserroles/${userSub}` );
      return data;
    } catch (e) {
      console.log(e.message);
    }
    return [];
  };

  const editNewUser = async () => {
    const rowData = {companyUuid: companyRecord.companyUuid, userClass: getUserClass(companyRecord), roles: [], newUser: true, department: ""}
    setDialogStates({showDialog: true, record: rowData, newUser: true, canEdit:props.canEdit})
  };

  const editUser = async (event, rowData) => {
    const roles =  await getUserRoles(rowData.user_id);
    rowData["roles"] = roles || [];
    const loginDate = moment(rowData?.last_login, "YYYY-MM-DDTHH:mm:ss");
    const lastLogin = rowData?.last_login === "Never" ? rowData?.last_login : loginDate.isValid() ?  loginDate.format("DD/MM/YYYY HH:mm") : "";
    rowData["lastLogin"] = lastLogin;
    setDialogStates({showDialog: true, record: rowData, newUser: false, canEdit:props.canEdit})
  };

  const handleClose = () => {
    setDialogStates({showDialog: false, record: null, newUser: false, canEdit:false});
    queryClient.invalidateQueries(["CompanyUsers"]);
  };

  const getColumns = (props) => {
    const baseColumn = {
      cellStyle: { padding: "8px 2px" },
      headerStyle: { padding: "8px 2px" },
      editable: props.canEdit ? "always" : "never",
      emptyValue: "",
      initialEditValue: "",
    };
    const formatChars={
      "U": "[A-Z]",
      "A": "[A-Z0-9]",
      "a": "[A-Za-z0-9]",
      "i": "[A-Za-z0-9\\-]",
      "9": "[0-9]",
      "D": "[0-9.]",
      "-": "-",
      "x": "."
    }
    const defaultEditProps = {
      style:{fontSize: "0.875rem"}
    };

    return [
      //{...validatorColumn},
      /*
        Can use ...ColumnWidth(70) to generate a fixed width column
      */
      {
        ...baseColumn,
        ...ColumnWidth(50),
        title: "",
        field: "picture",
        editable: "never",
        initialEditValue: "",
        render: rowData => (
          <img src={rowData.picture} style={{width: 40, borderRadius: '50%'}}/>
        ),
      },
      {
        ...baseColumn,
        title: "User Name",
        field: "name",
        editable: state.canEdit ? "onAdd" : "never",
        initialEditValue: "",
      },
      {
        ...baseColumn,
        ...ColumnWidth(150),
        title: "Forename",
        field: "firstName",
        editable: state.canEdit ? "always" : "never",
        initialEditValue: "",
      },
      {
        ...baseColumn,
        ...ColumnWidth(150),
        title: "Surname",
        field: "lastName",
        editable: state.canEdit ? "always" : "never",
        initialEditValue: "",
      },
      {
        ...baseColumn,
        ...ColumnWidth(150),
        title: "Department",
        field: "department",
        editable: state.canEdit ? "always" : "never",
        initialEditValue: "",
        render: (rowData) => (
          <ComboSelect
            id="department"
            group="DEPARTMENTS"
            subGroup=""
            fullWidth
            value={rowData.department}
            displayOnly={true}
          />
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(75),
        title: "Login count",
        field: "logins_count",
        editable: "never",
      },
      {
        ...baseColumn,
        ...ColumnWidth(110),
        title: "Last Login",
        field: "last_login",
        editable: "never",
        render: rowData => {
          if ( ! _.isEmpty(rowData.last_login)) {
            const lastLogin = moment(rowData.last_login).format("DD/MM/YYYY");
            return (<div>{lastLogin}</div>);
          }
          return (<div></div>);
        }
      },
      {
        ...baseColumn,
        ...ColumnWidth(50),
        title: "",
        field: "blocked",
        editable: "never",
        initialEditValue: "",
        render: rowData => (
          rowData.blocked ? <img src="images/blocked.png" style={{width: 40}}/> : <></>
        ),
      },
      {
        ...baseColumn,
        title: "Last IP",
        field: "last_ip",
        editable: "never",
      },
    ];
  };

  return (isAuthenticated &&
    <div style={{width: "100%", marginLeft: 20, marginRight: 20}}>
      <MaterialTable
        tableRef={tableRef}
        style={{
          border: "none",
          boxShadow: "none",
          borderRadius: 0,
        }}
        options={{
          addRowPosition: "first",
          actionsColumnIndex: -1,
          cellStyle: {
            padding: "8px 2px",
          },
          headerStyle: {
            backgroundColor: theme.palette.tableHeader.backgroundColor,
            color: theme.palette.tableHeader.textColor,
            padding: "2px",
          },
          padding: "dense",
          toolbarButtonAlignment: "right",
          pageSize: props.fullPage ? 15 : 5,
          detailPanelType: "single",
          minBodyHeight: props.fullPage ? "calc(100vh - 340px)" : undefined, //FIXME to calculate dynamic height, needed for correct scroll position identification
          maxBodyHeight: props.fullPage ? "calc(100vh - 340px)" : undefined
        }}
        components={{
          ...defaultComponents,
          Pagination: (props) => (
              <TablePagination
                  {...props}
                  rowsPerPageOptions={[5, 10, 15]}
              />
          ),
        }}
        actions={
          [
            (rowData) => ({
              //hidden: !state.canEdit ,
              disabled: !state.canEdit,
              icon: 'edit',
              tooltip: "Edit",
              isFreeAction: false,
              onClick: editUser
            }),
            {
              hidden: !state.canEdit ,
              disabled: !state.canEdit,
              icon: 'add',
              tooltip: "Add a new System User",
              isFreeAction: true,
              onClick: editNewUser
            },
          ]
        }
        title={"System Users" + (companyRecord.companyName === undefined ? " (No Company Selected)" : " for '" + companyRecord.companyName + "'")}
        columns={getColumns({canEdit: state.canEdit})}
        // detailPanel={
        //   [
        //     {
        //       tooltip: "Contact Methods",
        //       icon: 'mail',
        //       openIcon: 'drafts',
        //       render: (rowData) => {
        //         return (
        //           <div >
        //             <div style={{backgroundColor: "#f2f2f2"}}>
        //               <ContactMethods
        //                 canEdit={state.canEdit}
        //                 contactMethods={rowData.contactMethods}
        //                 onChange={(changedData) => {detailsChangeHandler(changedData, rowData, !state.canEdit)}}
        //                 extractContacts={extractMethods}
        //               />
        //             </div>
        //           </div>
        //         );
        //       },
        //     }
        //   ]
        // }
        data={companyUsers.data}
        editable={{
          isEditable: () => {
            return false;
          },
          isDeletable: () => {
            return state.canEdit;
          },
          onRowDelete: (oldData) =>
              new Promise((resolve, reject) => {
                handleDeleteCompanyUser.mutate({oldRecord: oldData, resolve, reject, extraKeys});
              }),
        }}
      />
      {dialogStates.showDialog
        ?
          <UserDetails
            record={dialogStates.record}
            canEdit={props.canEdit}
            newUser={dialogStates.newUser}
            classes={classes}
            dialogStates={dialogStates}
            showFullPage={false}
            fullPageStyle={{backgroundColor: "#e1e1e1"}}
            close={handleClose}
          />
        :
          <></>
      }

    </div>
  );

}

export default CompanyUserList;
